"use client";

import * as Sentry from "@sentry/browser";
import { SignOut } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { ErrorScreen } from "@/components/feedback/error-screen";
import DiscordReportButton from "@/components/integrations/discord/discord-report-button";
import { SignOutDialog } from "@/components/layout/sidebar/sign-out-dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/client";
import { isChunkLoadError, reloadOnceForUpdate } from "@/utils/version";

export default function GlobalError({ error: _error }: { error: Error }) {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);
  const { t } = useTranslation("global-error");

  const isChunk = useMemo(() => {
    const text = `${_error?.name ?? ""} ${_error?.message ?? ""}`;
    return isChunkLoadError(text);
  }, [_error]);
  useEffect(() => {
    if (isChunk) {
      reloadOnceForUpdate();
    } else {
      Sentry.captureException(_error);
      console.error("Global error caught:", _error, JSON.stringify(_error, null, 2));
    }
  }, [isChunk, _error]);
  if (isChunk) {
    return null;
  }

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      window.location.href = "/auth/callback/signout";
    } catch (_error) {
      setIsSigningOut(false);
    }
  };

  const openSignOutDialog = () => {
    setIsSignOutDialogOpen(true);
  };

  const closeSignOutDialog = () => {
    setIsSignOutDialogOpen(false);
  };

  return (
    <html lang="en">
      <body>
        <ErrorScreen
          titleKey="title"
          messageKey="criticalMessage"
          variant="critical"
          showGoBackButton={false}
          showHomeButton={true}
        >
          <Button
            onClick={openSignOutDialog}
            className="w-full max-w-sm mx-auto flex items-center justify-center bg-red-600 hover:bg-red-700 text-white border-0"
            size="lg"
          >
            <SignOut className="mr-2 h-5 w-5" />
            {t("actions.signOut")}
          </Button>
          <div className="w-full max-w-sm mx-auto mt-3">
            <DiscordReportButton error={_error} />
          </div>
        </ErrorScreen>

        <SignOutDialog
          isOpen={isSignOutDialogOpen}
          onClose={closeSignOutDialog}
          onConfirm={handleSignOut}
          isSigningOut={isSigningOut}
        />
      </body>
    </html>
  );
}
